import type {
    BoardCollapsedChange,
    BoardColumnDef,
    BoardLaneDef,
    BoardPlacement,
} from "./Board.types";

/** Collapsed columns and lanes, plus toggles when the surface allows them. */
export interface BoardCollapse {
    columnIds: ReadonlySet<string>;
    laneIds: ReadonlySet<string>;
    onToggleColumn?: (columnId: string) => void;
    onToggleLane?: (laneId: string) => void;
}

interface CreateBoardCollapseArgs {
    collapsedColumnIds?: readonly string[];
    collapsedLaneIds?: readonly string[];
    onCollapsedChange?: (next: BoardCollapsedChange) => void;
}

const toggleId = (ids: readonly string[], id: string) =>
    ids.includes(id) ? ids.filter((other) => other !== id) : [...ids, id];

export const createBoardCollapse = ({
    collapsedColumnIds = [],
    collapsedLaneIds = [],
    onCollapsedChange,
}: CreateBoardCollapseArgs): BoardCollapse => ({
    columnIds: new Set(collapsedColumnIds),
    laneIds: new Set(collapsedLaneIds),
    onToggleColumn:
        onCollapsedChange &&
        ((columnId) =>
            onCollapsedChange({
                collapsedColumnIds: toggleId(collapsedColumnIds, columnId),
                collapsedLaneIds: [...collapsedLaneIds],
            })),
    onToggleLane:
        onCollapsedChange &&
        ((laneId) =>
            onCollapsedChange({
                collapsedColumnIds: [...collapsedColumnIds],
                collapsedLaneIds: toggleId(collapsedLaneIds, laneId),
            })),
});

const COLUMN_TRACK = "var(--board-column-width)";
const COLLAPSED_TRACK = "var(--board-collapsed-column-width)";

/** One grid track per column; collapsed columns get a narrow strip. */
export const getColumnTemplate = (
    columns: readonly BoardColumnDef[],
    collapse: BoardCollapse,
) =>
    columns
        .map(({ id }) =>
            collapse.columnIds.has(id) ? COLLAPSED_TRACK : COLUMN_TRACK,
        )
        .join(" ");

/** The lanes a board renders: one null lane when it has none. */
export const getLaneIds = (lanes: readonly BoardLaneDef[] | undefined) =>
    lanes === undefined ? [null] : lanes.map(({ id }) => id);

/** Every cell a card could land in, collapsed or not. */
export const getAllPlacements = (
    columns: readonly BoardColumnDef[],
    lanes: readonly BoardLaneDef[] | undefined,
): BoardPlacement[] =>
    getLaneIds(lanes).flatMap((laneId) =>
        columns.map(({ id }) => ({ columnId: id, laneId })),
    );

/** Every cell column by column, lanes top to bottom: the order a group keeps. */
export const getBoardCellOrder = (
    columns: readonly BoardColumnDef[],
    lanes: readonly BoardLaneDef[] | undefined,
): BoardPlacement[] =>
    columns.flatMap((column) => getAllPlacements([column], lanes));

/** The expanded columns, each with its index among all columns (its grid track). */
export const getExpandedColumns = (
    columns: readonly BoardColumnDef[],
    collapse: BoardCollapse,
) =>
    columns.flatMap((column, columnIndex) =>
        collapse.columnIds.has(column.id) ? [] : [{ column, columnIndex }],
    );

interface ExpandedIdsArgs {
    columns: readonly BoardColumnDef[];
    lanes?: readonly BoardLaneDef[];
    collapse: BoardCollapse;
}

/** The columns and lanes keyboard moves step through: the expanded ones. */
export const getExpandedIds = ({
    columns,
    lanes,
    collapse,
}: ExpandedIdsArgs) => ({
    columnIds: getExpandedColumns(columns, collapse).map(
        ({ column }) => column.id,
    ),
    laneIds: getLaneIds(lanes).filter(
        (laneId) => laneId === null || !collapse.laneIds.has(laneId),
    ),
});

interface StepThroughArgs<TId> {
    ids: readonly TId[];
    id: TId;
    /** 1 forward, -1 back. */
    step: 1 | -1;
    /** Ids to skip return false. Defaults to taking any. */
    accepts?: (next: TId) => boolean;
}

/**
 * The nearest id `step` away from `id` that `accepts` takes, or undefined
 * at the end. Keyboard moves and focus step through expanded ids.
 */
export const stepThrough = <TId>({
    ids,
    id,
    step,
    accepts = () => true,
}: StepThroughArgs<TId>) => {
    for (
        let index = ids.indexOf(id) + step;
        index >= 0 && index < ids.length;
        index += step
    ) {
        if (accepts(ids[index])) {
            return { id: ids[index] };
        }
    }
    return undefined;
};

export const getLaneLabel = (lane: BoardLaneDef) =>
    lane.label ?? (typeof lane.title === "string" ? lane.title : lane.id);
